import Bowser from "bowser";

export interface BrowserInfo {
  name: string;
  version: string;
}

export interface OsInfo {
  name: string;
  version: string;
}

export type PlatformType = "desktop" | "mobile" | "tablet";

/**
 * Browser/OS/platform detection using Bowser.
 * Mirrors Android DeviceCollector's device info gathering.
 */
export class BrowserDetector {
  private static parser: Bowser.Parser.Parser | null = null;

  private static getParser(): Bowser.Parser.Parser {
    if (!BrowserDetector.parser) {
      BrowserDetector.parser = Bowser.getParser(navigator.userAgent);
    }
    return BrowserDetector.parser;
  }

  static getBrowser(): BrowserInfo {
    const browser = BrowserDetector.getParser().getBrowser();
    return {
      name: browser.name ?? "Unknown",
      version: browser.version ?? "Unknown",
    };
  }

  static getOS(): OsInfo {
    const os = BrowserDetector.getParser().getOS();
    return {
      name: os.name ?? "Unknown",
      version: os.version ?? "Unknown",
    };
  }

  static getPlatform(): PlatformType {
    const type = BrowserDetector.getParser().getPlatformType();
    if (type === "mobile") return "mobile";
    if (type === "tablet") return "tablet";
    return "desktop";
  }

  static getLanguage(): string {
    return navigator.language || "unknown";
  }

  static getTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "unknown";
    }
  }

  /**
   * Detect if the browser looks suspicious (bot, headless, automated).
   * Returns true if known bot user-agent patterns are detected.
   */
  static isSuspiciousBrowser(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    const botPatterns = [
      "bot",
      "crawler",
      "spider",
      "headless",
      "phantom",
      "selenium",
      "puppeteer",
      "playwright",
      "webdriver",
      "electron",
    ];
    return botPatterns.some((p) => ua.includes(p));
  }

  /**
   * Count headless/bot indicators in the current environment.
   */
  static getHeadlessIndicatorCount(): number {
    let count = 0;

    // webdriver flag
    if (navigator.webdriver) count++;

    // no languages
    if (!navigator.languages || navigator.languages.length === 0) count++;

    // no plugins
    if (!navigator.plugins || navigator.plugins.length === 0) count++;

    // suspicious user-agent patterns
    if (BrowserDetector.isSuspiciousBrowser()) count++;

    // WebGL availability check
    try {
      const canvas = document.createElement("canvas");
      const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
      if (!gl) count++;
    } catch {
      count++;
    }

    // screen dimensions anomaly (0x0 or very small)
    if (screen.width === 0 || screen.height === 0) count++;

    return count;
  }

  /** Force parser re-creation (e.g. after UA change in tests). */
  static reset(): void {
    BrowserDetector.parser = null;
  }
}
